import { CheckoutPage } from '@liveauctioneers/hammer-ui-payments/checkout';
import { ComponentProps, useEffect, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import ItemCard from '@/components/GridItemCard/Card/Card';
import { CarouselTimedOrCategorySearchCard } from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrCategorySearchCard/TimedOrCategorySearchCard';
import IntlProvider from '@/components/IntlRedux/IntlProvider';
import { MyFavoritesItemStatusTab } from '@/pages/MyItems/MyFavorites/types/MyFavoritesPage.types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchSavedItems } from '@/redux/modules/item/saved/savedItems.actions';
import { getSavedItemsItemIds } from '@/redux/modules/item/saved/savedItems.selectors';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { fetchRecommendedItemIfNeeded, getSortedRecommendedItemIdsFromState } from '@/redux/modules/recommendedItem';
import { GlobalState } from '@/redux/store';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { LotRecommendationType } from '@/utils/amplitude/analytics';
import { useTrackViewAllClicked, ViewAllSource } from '@/utils/amplitude/analytics/browse/trackViewAllClicked';
import { useTrackLotRecommendationSeen } from '@/utils/amplitude/analytics/item/trackLotRecommendationSeen.analytics';
import { getRecommendedItemsUrl, getSavedItemsUrl } from '@/utils/urls';

const CAROUSEL_CARD_COUNT = 4;

export const useGetViewMoreItemsInfo = (): ComponentProps<typeof CheckoutPage>['viewMoreItemsInfo'] => {
    const dispatch = useAppDispatch();

    const trackLotRecommendationSeen = useTrackLotRecommendationSeen();
    const trackViewAllClicked = useTrackViewAllClicked();
    const savedItemIds = useAppSelector(getSavedItemsItemIds);

    const recommendedItemIds = useAppSelector((state: GlobalState) =>
        getSortedRecommendedItemIdsFromState(state, CAROUSEL_CARD_COUNT)
    );

    const [viewMoreItemsCards, setViewMoreItemsCards] = useState<
        ComponentProps<typeof CheckoutPage>['viewMoreItemsInfo']['viewMoreItemsCards']
    >([]);

    useEffect(() => {
        dispatch(
            fetchSavedItems({
                page: 1,
                // @ts-expect-error In this very specific instance, we allow 4 for just the size of the carousel
                pageSize: CAROUSEL_CARD_COUNT,
                sort: SearchSortAndDirection.TimeStartingSoonest,
                status: MyFavoritesItemStatusTab.Available,
            })
        );

        // if we don't have enough saved items, switch to recommend items instead
        dispatch(fetchRecommendedItemIfNeeded());
    }, [dispatch]);

    useDeepCompareEffect(() => {
        const itemIds = savedItemIds.length >= CAROUSEL_CARD_COUNT ? savedItemIds : recommendedItemIds;
        if (itemIds?.length) {
            dispatch(
                fetchItemSummariesIfNeeded({
                    identifier: 'confirmation-page-ready-for-your-next-win',
                    itemIds,
                })
            ).then(() => {
                setViewMoreItemsCards(
                    itemIds.map((itemId, index) => {
                        const key = `${itemId}-view-more-items-carousel`;
                        return {
                            card: (
                                <IntlProvider>
                                    <ItemCard
                                        hideCardMenu
                                        itemCardOption={CarouselTimedOrCategorySearchCard}
                                        itemId={itemId}
                                        key={key}
                                        recommendationPosition={index + 1}
                                        recommendationType={
                                            savedItemIds.length >= CAROUSEL_CARD_COUNT
                                                ? LotRecommendationType.SavedLots
                                                : LotRecommendationType.RecommendedItems
                                        }
                                    />
                                </IntlProvider>
                            ),
                            key,
                        };
                    })
                );
            });
        }
    }, [dispatch, recommendedItemIds, savedItemIds]);

    if (viewMoreItemsCards.length === 0) {
        return;
    }

    return {
        trackCarouselSeen: () => {
            trackLotRecommendationSeen({
                recommendationIds: savedItemIds.length >= CAROUSEL_CARD_COUNT ? savedItemIds : recommendedItemIds,
                recommendationName:
                    savedItemIds.length >= CAROUSEL_CARD_COUNT
                        ? LotRecommendationType.RecommendedFromSaves
                        : LotRecommendationType.RecentlyViewed,
            });
        },
        viewMoreAction: {
            onClick: () => {
                trackViewAllClicked(
                    savedItemIds.length >= CAROUSEL_CARD_COUNT ? ViewAllSource.SavedLots : ViewAllSource.RecommendedLots
                );
            },
            path: savedItemIds.length >= CAROUSEL_CARD_COUNT ? getSavedItemsUrl() : getRecommendedItemsUrl(),
        },
        viewMoreItemsCards,
        viewMoreTitle:
            savedItemIds.length >= CAROUSEL_CARD_COUNT ? 'Ready for Your Next Win?' : 'Recommended Finds for You',
    };
};
